import { animate, style, transition, trigger } from '@angular/animations';
import { Component, HostBinding, computed, inject, input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { ECustomFieldType } from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { snakeCase } from 'lodash';
import { EAdvancedSettings } from '../deliverable-advanced-settings.interface';
import { DeliverableAdvancedSettingsService } from '../deliverable-advanced-settings.service';

@Component({
  animations: [
    trigger('advancedSettingsEnter', [
      transition(':enter', [
        style({ height: 0, marginTop: 0, opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', marginTop: '16px', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, marginTop: 0, opacity: 0 })),
      ]),
    ]),
  ],
  imports: [IconComponent, MatButtonModule, MatSlideToggleModule, ReactiveFormsModule, TranslateModule],
  selector: 'app-deliverable-advanced-settings-section',
  templateUrl: './deliverable-advanced-settings-section.component.html',
})
export class DeliverableAdvancedSettingsSectionComponent {
  @HostBinding('@advancedSettingsEnter') public readonly animationTrigger = '';

  public readonly form = input.required<FormGroup>();
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly deliverableType = input.required<EDeliverableType>();
  public readonly isCustomMode = input<boolean>(false);
  public readonly creatorId = input<string | null | undefined>(null);
  public readonly isViewMode = input<boolean>(false);

  public readonly EAdvancedSettings = EAdvancedSettings;

  private readonly customValuesService = inject(CustomValuesService);
  private readonly advancedSettingsService = inject(DeliverableAdvancedSettingsService);

  public readonly availableSettings = computed(() =>
    this.advancedSettingsService.getAdvancedSettings(this.platformFeature(), this.deliverableType()),
  );

  public readonly hasSponsoredContentRelevant = computed(() =>
    this.advancedSettingsService.hasAdvancedSetting(
      this.platformFeature(),
      this.deliverableType(),
      EAdvancedSettings.sponsoredContentRelevant,
    ),
  );

  public get sponsoredContentRelevantControl(): FormControl<boolean> {
    return this.form().get('sponsoredContentRelevant') as FormControl<boolean>;
  }

  public hasSponsoredContentCustomValue(): boolean {
    const creatorId = this.creatorId();

    if (!creatorId) {
      return false;
    }

    const customCtaValues = this.form().get('customCtaValues')?.value as ICustomCtaValue[] | undefined;

    return (
      customCtaValues?.some(
        (v) => v.creatorId === creatorId && v.valueName === snakeCase(EAdvancedSettings.sponsoredContentRelevant),
      ) ?? false
    );
  }

  public onRevertSponsoredContentRelevant(): void {
    this.customValuesService.revertToGlobalValue(
      this.form(),
      EAdvancedSettings.sponsoredContentRelevant,
      this.creatorId()!,
      ECustomFieldType.boolean,
    );
  }
}
